import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../components/Layout';
import { pl, tax } from '../services/api';
import { TrendingUp, TrendingDown, Download, Minus, BarChart3 } from 'lucide-react';
import { useToast } from '../context/ToastContext';

const PL: React.FC = () => {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [provisional, setProvisional] = useState(false);
  const [data, setData] = useState<any>(null);
  const [products, setProducts] = useState<any[]>([]);
  const [mode, setMode] = useState<'single' | 'range'>('single');
  const [fromMonth, setFromMonth] = useState(now.getMonth() + 1);
  const [fromYear, setFromYear] = useState(now.getFullYear());
  const [toMonth, setToMonth] = useState(now.getMonth() + 1);
  const [toYear, setToYear] = useState(now.getFullYear());
  const [rematching, setRematching] = useState(false);
  const { toast } = useToast();

  const reshape = (d: any) => ({
    ...d,
    cogs: { total: d.cogs_total || 0, breakdown: d.cogs_breakdown || {}, percentage: d.cogs_percentage || 0 },
    shipping: { total: d.shipping_total || 0, breakdown: d.shipping_breakdown || {}, percentage: d.shipping_percentage || 0 },
    gateway: { total: d.gateway_total || 0, breakdown: d.gateway_breakdown || {}, percentage: d.gateway_percentage || 0 },
    marketing: { total: d.marketing_total || 0, breakdown: d.marketing_breakdown || {}, percentage: d.marketing_percentage || 0 },
    fixed: { total: d.fixed_total || 0, breakdown: d.fixed_breakdown || {}, percentage: d.fixed_percentage || 0 },
    indirect_expenses: { total: d.fixed_total || 0, breakdown: d.fixed_breakdown || {}, percentage: d.fixed_percentage || 0 },
    total_expenses: d.total_expenses || 0,
  });

  useEffect(() => {
    setData(null);
    if (mode === 'single') {
      pl.getProducts(year, month).then(r => setProducts(r.data)).catch(() => setProducts([]));
      pl.get(year, month, provisional).then(r => setData(reshape(r.data))).catch(() => setData(null));
    } else {
      pl.getRangeProducts(fromYear, fromMonth, toYear, toMonth).then(r => setProducts(r.data)).catch(() => setProducts([]));
      pl.getRange(fromYear, fromMonth, toYear, toMonth, provisional).then(r => setData(reshape(r.data))).catch(() => setData(null));
    }
  }, [mode, month, year, fromMonth, fromYear, toMonth, toYear, provisional]);

  const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];

  const handleExportExcel = async () => {
    const res = await tax.export(year, month);
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = `futuresaints_${monthNames[month - 1]}_${year}_sales.xlsx`;
    a.click();
  };

  const handleRematch = useCallback(async () => {
    setRematching(true);
    try {
      const res = await pl.rematchInventory();
      const { matched, still_unmatched } = res.data;
      if (matched > 0) {
        toast(`Matched ${matched} item${matched !== 1 ? 's' : ''}${still_unmatched > 0 ? `, ${still_unmatched} still unmatched` : ' — all resolved'}`, 'success');
      } else {
        toast(`No new matches found — ${still_unmatched} item${still_unmatched !== 1 ? 's' : ''} still unmatched`, 'error');
      }
      if (mode === 'single') {
        pl.get(year, month, provisional).then(r => setData(reshape(r.data))).catch(() => {});
      } else {
        pl.getRange(fromYear, fromMonth, toYear, toMonth, provisional).then(r => setData(reshape(r.data))).catch(() => {});
      }
    } catch {
      toast('Rematch failed', 'error');
    } finally {
      setRematching(false);
    }
  }, [mode, year, month, fromYear, fromMonth, toYear, toMonth, provisional]);

  const inputStyle: React.CSSProperties = {
    padding: '9px 14px', background: 'var(--bg-card)', border: '1px solid var(--border)',
    borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)', fontSize: 13, outline: 'none',
  };
  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  const pctColor = (pct: number) => pct > 50 ? 'var(--accent-red)' : pct > 30 ? 'var(--accent-amber)' : 'var(--accent-green)';

  return (
    <Layout title="P&L Report" subtitle={data ? (mode === 'single' ? `${MONTHS[month-1]} ${year}` : `${MONTHS[fromMonth-1]} ${fromYear} – ${MONTHS[toMonth-1]} ${toYear}`) + ' — Revenue: All Paid | COGS: All Fulfilled' : undefined}>
      <div className="animate-in" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: provisional ? 12 : 24, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {(['single', 'range'] as const).map(m => (
            <button key={m} onClick={() => setMode(m)} style={{
              padding: '9px 14px', border: `1px solid ${mode === m ? 'var(--accent-blue)' : 'var(--border)'}`,
              borderRadius: 'var(--radius-sm)', background: mode === m ? 'rgba(99,102,241,0.12)' : 'var(--bg-card)',
              color: mode === m ? 'var(--accent-blue)' : 'var(--text-muted)',
              fontSize: 12, fontWeight: 600, cursor: 'pointer',
            }}>{m === 'single' ? 'Single Month' : 'Date Range'}</button>
          ))}
          {mode === 'single' ? (
            <>
              <select value={month} onChange={e => setMonth(Number(e.target.value))} style={inputStyle}>
                {MONTHS.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
              </select>
              <input type="number" value={year} onChange={e => setYear(Number(e.target.value))} style={{ ...inputStyle, width: 90 }} />
            </>
          ) : (
            <>
              {[
                { label: '3M', action: () => { const d = new Date(); d.setMonth(d.getMonth()-2); setFromMonth(d.getMonth()+1); setFromYear(d.getFullYear()); setToMonth(now.getMonth()+1); setToYear(now.getFullYear()); } },
                { label: '6M', action: () => { const d = new Date(); d.setMonth(d.getMonth()-5); setFromMonth(d.getMonth()+1); setFromYear(d.getFullYear()); setToMonth(now.getMonth()+1); setToYear(now.getFullYear()); } },
                { label: 'YTD', action: () => { setFromMonth(1); setFromYear(now.getFullYear()); setToMonth(now.getMonth()+1); setToYear(now.getFullYear()); } },
              ].map(({ label, action }) => (
                <button key={label} onClick={action} style={{
                  padding: '7px 11px', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
                  background: 'var(--bg-card)', color: 'var(--text-muted)', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                }}>{label}</button>
              ))}
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>From</span>
              <select value={fromMonth} onChange={e => setFromMonth(Number(e.target.value))} style={inputStyle}>
                {MONTHS.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
              </select>
              <input type="number" value={fromYear} onChange={e => setFromYear(Number(e.target.value))} style={{ ...inputStyle, width: 90 }} />
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>To</span>
              <select value={toMonth} onChange={e => setToMonth(Number(e.target.value))} style={inputStyle}>
                {MONTHS.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
              </select>
              <input type="number" value={toYear} onChange={e => setToYear(Number(e.target.value))} style={{ ...inputStyle, width: 90 }} />
            </>
          )}
          <button onClick={() => setProvisional(p => !p)} style={{
            padding: '9px 14px', border: `1px solid ${provisional ? 'var(--accent-amber)' : 'var(--border)'}`,
            borderRadius: 'var(--radius-sm)', background: provisional ? 'rgba(251,191,36,0.12)' : 'var(--bg-card)',
            color: provisional ? 'var(--accent-amber)' : 'var(--text-muted)',
            fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
          }}>Provisional</button>
        </div>
        <button onClick={handleExportExcel} style={{
          padding: '9px 18px', background: 'linear-gradient(135deg, var(--accent-green), #059669)',
          border: 'none', borderRadius: 'var(--radius-sm)', color: '#fff', fontSize: 13, fontWeight: 600,
          cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7,
        }}><Download size={14} /> Tax Excel</button>
      </div>

      {provisional && (
        <div style={{
          marginBottom: 20, padding: '10px 16px', borderRadius: 'var(--radius-sm)',
          background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.3)',
          fontSize: 12, color: 'var(--accent-amber)', fontWeight: 500,
        }}>
          Provisional — COGS estimated from inventory cost prices for all delivered orders
        </div>
      )}

      {data && data.unmatched_units > 0 && (
        <div style={{
          marginBottom: 20, padding: '10px 16px', borderRadius: 'var(--radius-sm)',
          background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.3)',
          fontSize: 12, color: 'var(--accent-amber)', fontWeight: 500,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        }}>
          <span>{data.unmatched_units} unit{data.unmatched_units !== 1 ? 's' : ''} not matched to inventory — excluded from product breakdown</span>
          <button
            onClick={handleRematch}
            disabled={rematching}
            style={{
              padding: '5px 12px', background: 'rgba(251,191,36,0.18)', border: '1px solid rgba(251,191,36,0.4)',
              borderRadius: 'var(--radius-sm)', color: 'var(--accent-amber)', fontSize: 11, fontWeight: 700,
              cursor: rematching ? 'not-allowed' : 'pointer', opacity: rematching ? 0.6 : 1, whiteSpace: 'nowrap',
            }}
          >
            {rematching ? 'Rematching…' : 'Rematch'}
          </button>
        </div>
      )}

      {data && (
        <div style={{ display: 'grid', gridTemplateColumns: '680px 1fr', gap: 20, alignItems: 'start' }}>
          <div>
          {/* Main P&L Card */}
          <div className="animate-in-1" style={{
            background: 'var(--bg-card)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)', overflow: 'hidden',
          }}>
            {/* REVENUE */}
            <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600, marginBottom: 4 }}>
                    Revenue (Ex-GST)
                  </div>
                  <div style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>{data.paid_orders} orders · GST: ₹{data.gst_amount?.toLocaleString('en-IN')}</div>
                </div>
                <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--accent-green)', letterSpacing: -0.5 }}>
                  ₹{(data.revenue_exclusive ?? data.revenue).toLocaleString('en-IN')}
                </div>
              </div>
            </div>

            {/* COGS Section */}
            <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent-amber)', textTransform: 'uppercase', letterSpacing: 0.8 }}>
                  COGS (Direct Expenses)
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                    {data.cogs.percentage}%
                  </span>
                  <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--accent-red)' }}>−₹{data.cogs.total.toLocaleString('en-IN')}</span>
                </div>
              </div>
              {Object.entries(data.cogs.breakdown || {}).map(([cat, info]: any) => (
                <div key={cat}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0 7px 16px' }}>
                    <span style={{ fontSize: 13, color: 'var(--text-secondary)', textTransform: 'capitalize' }}>{cat}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <span style={{ fontSize: 11, color: 'var(--text-muted)', minWidth: 40, textAlign: 'right' }}>{info.percentage}%</span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', minWidth: 80, textAlign: 'right' }}>₹{info.amount.toLocaleString('en-IN')}</span>
                    </div>
                  </div>
                  {info.items && info.items.length > 0 && (
                    <div style={{ marginLeft: 32, marginBottom: 4, borderLeft: '2px solid var(--border)', paddingLeft: 12 }}>
                      {info.items.map((item: any) => (
                        <div key={item.article_type} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '3px 0' }}>
                          <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{item.article_type}</span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            {item.qty != null && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{item.qty} pcs</span>}
                            <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--text-secondary)' }}>₹{item.amount.toLocaleString('en-IN')}</span>
                          </div>
                        </div>
                      ))}
                      {info.items.some((i: any) => i.qty != null) && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', marginTop: 2, borderTop: '1px solid var(--border)' }}>
                          <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>Total</span>
                          <span style={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 700 }}>
                            {info.items.reduce((s: number, i: any) => s + (i.qty || 0), 0)} pcs
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {Object.keys(data.cogs.breakdown || {}).length === 0 && (
                <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: '4px 0 4px 16px' }}>No COGS recorded</div>
              )}
            </div>

            {/* GROSS PROFIT */}
            <div style={{ padding: '14px 24px', borderBottom: '1px solid var(--border)', background: 'var(--bg-elevated)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>Gross Profit</div>
                  <div style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>Revenue − COGS</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 18, fontWeight: 800, color: data.gross_profit >= 0 ? 'var(--accent-green)' : 'var(--accent-red)', letterSpacing: -0.3 }}>
                    {data.gross_profit < 0 ? '−' : ''}₹{Math.abs(data.gross_profit).toLocaleString('en-IN')}
                  </div>
                  <div style={{ fontSize: 11, color: pctColor(100 - data.gross_margin), fontWeight: 600 }}>{data.gross_margin}% margin</div>
                </div>
              </div>
            </div>

            {/* SHIPPING + GATEWAY */}
            {(['shipping', 'gateway'] as const).map(key => {
              const sec = data[key];
              if (!sec || sec.total === 0) return null;
              const labels: Record<string, string> = { shipping: 'Shipping', gateway: 'Gateway Fees' };
              const colors: Record<string, string> = { shipping: 'var(--accent-green)', gateway: 'var(--accent-purple)' };
              return (
                <div key={key} style={{ padding: '12px 24px', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: Object.keys(sec.breakdown).length > 0 ? 8 : 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: colors[key], textTransform: 'uppercase', letterSpacing: 0.8 }}>{labels[key]}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{sec.percentage}%</span>
                      <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--accent-red)' }}>−₹{sec.total.toLocaleString('en-IN')}</span>
                    </div>
                  </div>
                  {Object.entries(sec.breakdown || {}).map(([cat, info]: any) => (
                    <div key={cat} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0 5px 16px' }}>
                      <span style={{ fontSize: 12.5, color: 'var(--text-secondary)', textTransform: 'capitalize' }}>{cat}</span>
                      <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)' }}>₹{info.amount.toLocaleString('en-IN')}</span>
                    </div>
                  ))}
                </div>
              );
            })}

            {/* CONTRIBUTION MARGIN */}
            <div style={{ padding: '14px 24px', borderBottom: '1px solid var(--border)', background: 'var(--bg-elevated)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>Contribution Margin</div>
                  <div style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>Revenue − COGS − Shipping − Gateway</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 18, fontWeight: 800, color: data.contribution_margin >= 0 ? 'var(--accent-green)' : 'var(--accent-red)', letterSpacing: -0.3 }}>
                    {data.contribution_margin < 0 ? '−' : ''}₹{Math.abs(data.contribution_margin).toLocaleString('en-IN')}
                  </div>
                  <div style={{ fontSize: 11, color: pctColor(100 - data.contribution_margin_pct), fontWeight: 600 }}>{data.contribution_margin_pct}% margin</div>
                </div>
              </div>
            </div>

            {/* MARKETING */}
            {data.marketing && data.marketing.total > 0 && (
              <div style={{ padding: '12px 24px', borderBottom: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#f97316', textTransform: 'uppercase', letterSpacing: 0.8 }}>Marketing</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{data.marketing.percentage}%</span>
                    <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--accent-red)' }}>−₹{data.marketing.total.toLocaleString('en-IN')}</span>
                  </div>
                </div>
                {Object.entries(data.marketing.breakdown || {}).map(([cat, info]: any) => (
                  <div key={cat} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0 5px 16px' }}>
                    <span style={{ fontSize: 12.5, color: 'var(--text-secondary)', textTransform: 'capitalize' }}>{cat}</span>
                    <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)' }}>₹{info.amount.toLocaleString('en-IN')}</span>
                  </div>
                ))}
              </div>
            )}

            {/* MARKETING CONTRIBUTION */}
            {data.marketing && data.marketing.total > 0 && (
              <div style={{ padding: '14px 24px', borderBottom: '1px solid var(--border)', background: 'var(--bg-elevated)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>Marketing Contribution</div>
                    <div style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>Contribution Margin − Marketing</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 18, fontWeight: 800, color: data.marketing_contribution >= 0 ? 'var(--accent-green)' : 'var(--accent-red)', letterSpacing: -0.3 }}>
                      {data.marketing_contribution < 0 ? '−' : ''}₹{Math.abs(data.marketing_contribution).toLocaleString('en-IN')}
                    </div>
                    <div style={{ fontSize: 11, color: pctColor(100 - data.marketing_contribution_pct), fontWeight: 600 }}>{data.marketing_contribution_pct}% margin</div>
                  </div>
                </div>
              </div>
            )}

            {/* FIXED EXPENSES */}
            {data.fixed && data.fixed.total > 0 && (
              <div style={{ padding: '12px 24px', borderBottom: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent-blue)', textTransform: 'uppercase', letterSpacing: 0.8 }}>Fixed Expenses</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{data.fixed.percentage}%</span>
                    <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--accent-red)' }}>−₹{data.fixed.total.toLocaleString('en-IN')}</span>
                  </div>
                </div>
                {Object.entries(data.fixed.breakdown || {}).map(([cat, info]: any) => (
                  <div key={cat} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0 5px 16px' }}>
                    <span style={{ fontSize: 12.5, color: 'var(--text-secondary)', textTransform: 'capitalize' }}>{cat}</span>
                    <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)' }}>₹{info.amount.toLocaleString('en-IN')}</span>
                  </div>
                ))}
              </div>
            )}

            {/* NET PROFIT */}
            <div style={{ padding: '20px 24px', background: data.net_profit >= 0 ? 'linear-gradient(135deg, rgba(45,212,160,0.08), rgba(45,212,160,0.03))' : 'linear-gradient(135deg, rgba(239,90,111,0.08), rgba(239,90,111,0.03))' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--text-primary)' }}>Net Profit</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>Marketing Contribution − Fixed</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 28, fontWeight: 800, letterSpacing: -0.8, color: data.net_profit >= 0 ? 'var(--accent-green)' : 'var(--accent-red)' }}>
                    {data.net_profit < 0 ? '−' : ''}₹{Math.abs(data.net_profit).toLocaleString('en-IN')}
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 700, marginTop: 2, color: data.net_margin >= 0 ? 'var(--accent-green)' : 'var(--accent-red)' }}>
                    {data.net_margin}% net margin
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Summary Table */}
          <div className="animate-in-2" style={{
            background: 'var(--bg-card)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)', padding: 24, marginTop: 16,
          }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              <BarChart3 size={16} color="var(--accent-blue)" /> Summary
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  {['Particular', 'Amount (₹)', '% of Revenue'].map(h => (
                    <th key={h} style={{ padding: '8px 0', textAlign: h === 'Particular' ? 'left' : 'right', fontSize: 10.5, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  { label: 'Revenue (ex-GST)', amount: data.revenue_exclusive ?? data.revenue, pct: 100, color: 'var(--accent-green)', bold: true },
                  { label: 'COGS', amount: -data.cogs.total, pct: -data.cogs.percentage, color: 'var(--accent-red)', bold: false },
                  { label: 'Gross Profit', amount: data.gross_profit, pct: data.gross_margin, color: data.gross_profit >= 0 ? 'var(--accent-green)' : 'var(--accent-red)', bold: true },
                  { label: 'Shipping', amount: -(data.shipping?.total || 0), pct: -(data.shipping?.percentage || 0), color: 'var(--accent-red)', bold: false },
                  { label: 'Gateway Fees', amount: -(data.gateway?.total || 0), pct: -(data.gateway?.percentage || 0), color: 'var(--accent-red)', bold: false },
                  { label: 'Contribution Margin', amount: data.contribution_margin, pct: data.contribution_margin_pct, color: data.contribution_margin >= 0 ? 'var(--accent-green)' : 'var(--accent-red)', bold: true },
                  { label: 'Marketing', amount: -(data.marketing?.total || 0), pct: -(data.marketing?.percentage || 0), color: 'var(--accent-red)', bold: false },
                  { label: 'Mktg Contribution', amount: data.marketing_contribution, pct: data.marketing_contribution_pct, color: data.marketing_contribution >= 0 ? 'var(--accent-green)' : 'var(--accent-red)', bold: true },
                  { label: 'Fixed Expenses', amount: -(data.fixed?.total || 0), pct: -(data.fixed?.percentage || 0), color: 'var(--accent-red)', bold: false },
                  { label: 'Net Profit', amount: data.net_profit, pct: data.net_margin, color: data.net_profit >= 0 ? 'var(--accent-green)' : 'var(--accent-red)', bold: true },
                ].map((row, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '10px 0', fontSize: 13, fontWeight: row.bold ? 700 : 400, color: row.bold ? 'var(--text-primary)' : 'var(--text-secondary)' }}>{row.label}</td>
                    <td style={{ padding: '10px 0', textAlign: 'right', fontSize: 14, fontWeight: row.bold ? 700 : 500, color: row.color }}>
                      {row.amount < 0 ? '−' : ''}₹{Math.abs(row.amount).toLocaleString('en-IN')}
                    </td>
                    <td style={{ padding: '10px 0', textAlign: 'right', fontSize: 12, color: 'var(--text-muted)', fontWeight: 500 }}>{Math.abs(row.pct)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* PER ORDER */}
          {data.per_order && Object.keys(data.per_order).length > 0 && (
            <div className="animate-in-3" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 24, marginTop: 16 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 16 }}>Per Order — Ex-GST ({data.fulfilled_orders} fulfilled)</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                {[
                  { label: 'Revenue', value: data.per_order.revenue, color: 'var(--accent-green)' },
                  { label: 'COGS', value: data.per_order.cogs, color: 'var(--accent-amber)' },
                  { label: 'Shipping', value: data.per_order.shipping, color: 'var(--accent-green)' },
                  { label: 'Contribution', value: data.per_order.contribution, color: data.per_order.contribution >= 0 ? 'var(--accent-green)' : 'var(--accent-red)' },
                  { label: 'Marketing', value: data.per_order.marketing, color: '#f97316' },
                  { label: 'Mktg Contribution', value: data.per_order.marketing_contribution, color: data.per_order.marketing_contribution >= 0 ? 'var(--accent-green)' : 'var(--accent-red)' },
                ].map(({ label, value, color }) => (
                  <div key={label} style={{ background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)', padding: '12px 14px' }}>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 4, fontWeight: 600 }}>{label}</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color }}>{value < 0 ? '−' : ''}₹{Math.abs(value).toLocaleString('en-IN')}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          </div>

          {/* PRODUCT BREAKDOWN */}
          {products.length > 0 && (
            <div>
              <div className="animate-in-2" style={{
                background: 'var(--bg-card)', border: '1px solid var(--border)',
                borderRadius: 'var(--radius-lg)', overflow: 'hidden',
              }}>
                <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>
                  Product Breakdown
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border)' }}>
                      {['Category', 'Units', 'Revenue', 'COGS', 'GP', 'Margin'].map(h => (
                        <th key={h} style={{ padding: '8px 12px', textAlign: h === 'Category' ? 'left' : 'right', fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((p: any) => (
                      <tr key={p.article_type} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '9px 12px', fontSize: 12.5, color: 'var(--text-primary)', fontWeight: 500 }}>{p.article_type}</td>
                        <td style={{ padding: '9px 12px', textAlign: 'right', fontSize: 12, color: 'var(--text-muted)' }}>{p.units}</td>
                        <td style={{ padding: '9px 12px', textAlign: 'right', fontSize: 12, color: 'var(--text-secondary)' }}>₹{p.revenue.toLocaleString('en-IN')}</td>
                        <td style={{ padding: '9px 12px', textAlign: 'right', fontSize: 12, color: 'var(--text-muted)' }}>₹{p.cogs.toLocaleString('en-IN')}</td>
                        <td style={{ padding: '9px 12px', textAlign: 'right', fontSize: 12.5, fontWeight: 600, color: p.gross_profit >= 0 ? 'var(--accent-green)' : 'var(--accent-red)' }}>₹{p.gross_profit.toLocaleString('en-IN')}</td>
                        <td style={{ padding: '9px 12px', textAlign: 'right', fontSize: 12, fontWeight: 700, color: p.gross_margin >= 50 ? 'var(--accent-green)' : p.gross_margin >= 30 ? 'var(--accent-amber)' : 'var(--accent-red)' }}>{p.gross_margin}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {!data && (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)', fontSize: 14 }}>
          Select a month and year to view P&L report.
        </div>
      )}
    </Layout>
  );
};

export default PL;
